import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown, Lock, Check, X, Plus, KeyRound, Loader2, ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";
import { householdsApi, formatError } from "../lib/api";
import { HouseholdSwitchTransition } from "./HouseholdSwitchTransition";
import { getHouseholdIcon } from "../lib/householdIcons";

/**
 * MultiHouseholdPanel — sits at the top of the /household page.
 *
 *   • If user is a member of MORE THAN ONE household: shows a "Switch
 *     household" button (active household name + arrow). Tapping opens a
 *     sheet listing every membership; tap a row to swap the active one.
 *
 *   • Always shows two CTAs: "Create another household" + "Join another
 *     household". Free users see a lock icon and an upsell toast on tap.
 *     Premium users get a modal that wraps the existing create/join flow.
 *
 * Designed to be additive — the rest of the Household page (member list,
 * leave button) continues to render the active household as before.
 */
export const MultiHouseholdPanel = ({ user, activeHousehold, onChanged }) => {
  const isPremium = Boolean(user?.is_premium);
  const memberships = user?.household_ids || [];
  const [list, setList] = useState([]);
  const [sheet, setSheet] = useState(null); // null | "switch" | "create" | "join"
  const [busy, setBusy] = useState(false);
  const [createName, setCreateName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  // While truthy, the full-screen "leaving → entering" transition plays.
  // Cleared once the post-switch reload has settled AND the animation has
  // had at least 1.4s of screen time (whichever is longer). This is the
  // same UX as the Dashboard switcher so switching from anywhere in the
  // app feels identical.
  const [switching, setSwitching] = useState(null); // { from, to } | null

  // Lazy-load the full household list only when the user opens the switcher
  // (or when memberships count changes so the panel stays consistent).
  useEffect(() => {
    if (memberships.length <= 1) {
      setList([]);
      return;
    }
    householdsApi
      .all()
      .then((r) => setList(r.households || []))
      .catch(() => setList([]));
  }, [memberships.length, activeHousehold?.id]);

  const lockedTap = (label) => () => {
    toast.info(`${label} is a Premium feature`, {
      description: "Upgrade to manage multiple households.",
    });
  };

  const openLocked = (kind) => {
    if (!isPremium) {
      const label = kind === "create" ? "Creating another household" : "Joining another household";
      lockedTap(label)();
      return;
    }
    setSheet(kind);
  };

  const doSwitch = async (hid) => {
    if (hid === activeHousehold?.id) {
      setSheet(null);
      return;
    }
    const target = list.find((h) => h.id === hid);
    const fromName = activeHousehold?.name || null;
    const toName = target?.name || null;
    const fromIcon = activeHousehold?.icon || "home";
    const toIcon = target?.icon || "home";
    setBusy(true);
    setSheet(null); // close switcher sheet so overlay is over the full page
    setSwitching({ from: fromName, to: toName, fromIcon, toIcon });
    try {
      // Hold the overlay for at least the animation duration so it never
      // flashes by — runs in parallel with the API call + parent refetch.
      const minHold = new Promise((res) => setTimeout(res, 1400));
      const work = (async () => {
        await householdsApi.switch(hid);
        // `onChanged` is wired by Household.jsx → triggers a refetch of the
        // active household + member list. We wait for it to settle before
        // tearing down the overlay so the new context is fully painted.
        await onChanged?.();
      })();
      await Promise.all([minHold, work]);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
      setSwitching(null);
    }
  };

  const doCreate = async () => {
    const name = createName.trim();
    if (!name) {
      toast.error("Give the household a name");
      return;
    }
    setBusy(true);
    try {
      const r = await householdsApi.create(name);
      toast.success(`Created ${r.name}`);
      setSheet(null);
      setCreateName("");
      onChanged?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const doJoin = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4) {
      toast.error("Enter a valid join code");
      return;
    }
    setBusy(true);
    try {
      const r = await householdsApi.join(code);
      toast.success(`Joined ${r.name}`);
      setSheet(null);
      setJoinCode("");
      onChanged?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const closeSheet = () => {
    if (busy) return;
    setSheet(null);
  };

  return (
    <>
      {/* Switcher pill — only when user belongs to more than one household. */}
      {memberships.length > 1 && (() => {
        const ActiveIcon = getHouseholdIcon(activeHousehold?.icon);
        return (
          <button
            type="button"
            onClick={() => setSheet("switch")}
            data-testid="household-switcher-btn"
            className="mt-5 w-full tup-card p-3 flex items-center gap-3 hover:bg-[#F5F3ED] transition-colors"
          >
            <div className="w-9 h-9 rounded-lg brand-gradient-bg flex items-center justify-center shrink-0">
              <ActiveIcon className="w-4 h-4 text-white" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                Active household
              </div>
              <div className="font-semibold text-sm text-[#2D3142] truncate">
                {activeHousehold?.name || "—"}
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[#277A82] font-semibold">
              <ArrowRightLeft className="w-3.5 h-3.5" />
              Switch · {memberships.length}
            </div>
          </button>
        );
      })()}

      {/* Create / Join CTAs — always visible, gated for free users. */}
      <div className="mt-3 grid grid-cols-2 gap-2.5">
        <button
          type="button"
          onClick={() => openLocked("create")}
          data-testid="household-create-another-btn"
          className={`tup-card p-3 flex flex-col items-start gap-1 text-left transition-colors ${
            isPremium ? "hover:bg-[#F5F3ED]" : "opacity-90 hover:bg-[#F5F3ED]"
          }`}
        >
          <div className="flex items-center gap-1.5">
            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
              isPremium ? "bg-[#DCEDF0] text-[#1F5566]" : "bg-[#F1EEE5] text-[#8A93A6]"
            }`}>
              <Plus className="w-3.5 h-3.5" />
            </div>
            {!isPremium && <Lock className="w-3 h-3 text-[#8A93A6]" />}
          </div>
          <div className="font-semibold text-xs text-[#2D3142] mt-1">Create another</div>
          <div className="text-[10px] text-[#8A93A6] leading-snug">
            {isPremium ? "Start a fresh household" : "Premium feature"}
          </div>
        </button>

        <button
          type="button"
          onClick={() => openLocked("join")}
          data-testid="household-join-another-btn"
          className={`tup-card p-3 flex flex-col items-start gap-1 text-left transition-colors ${
            isPremium ? "hover:bg-[#F5F3ED]" : "opacity-90 hover:bg-[#F5F3ED]"
          }`}
        >
          <div className="flex items-center gap-1.5">
            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
              isPremium ? "bg-[#DCEDF0] text-[#1F5566]" : "bg-[#F1EEE5] text-[#8A93A6]"
            }`}>
              <KeyRound className="w-3.5 h-3.5" />
            </div>
            {!isPremium && <Lock className="w-3 h-3 text-[#8A93A6]" />}
          </div>
          <div className="font-semibold text-xs text-[#2D3142] mt-1">Join another</div>
          <div className="text-[10px] text-[#8A93A6] leading-snug">
            {isPremium ? "With a join code" : "Premium feature"}
          </div>
        </button>
      </div>

      {!isPremium && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-[#1F5566] bg-[#DCEDF0]/60 rounded-lg px-3 py-2"
             data-testid="household-multi-premium-hint">
          <Crown className="w-3.5 h-3.5 shrink-0" />
          <span>
            <strong>Premium</strong> lets you create or join unlimited households and switch between them.
          </span>
        </div>
      )}

      <AnimatePresence mode="wait">
        {sheet && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeSheet}
            className="fixed inset-0 z-[2147483600] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center"
            data-testid="household-sheet-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-5 sm:m-4 max-h-[78vh] overflow-y-auto"
              data-testid="household-sheet"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="font-heading text-lg font-semibold text-[#2D3142]">
                  {sheet === "switch" && "Switch household"}
                  {sheet === "create" && "Create another household"}
                  {sheet === "join" && "Join another household"}
                </div>
                <button
                  type="button"
                  onClick={closeSheet}
                  data-testid="household-sheet-close"
                  className="w-9 h-9 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {sheet === "switch" && (
                <ul className="space-y-2" data-testid="household-switch-list">
                  {list.map((h) => {
                    const RowIcon = getHouseholdIcon(h.icon);
                    return (
                    <li key={h.id}>
                      <button
                        type="button"
                        onClick={() => doSwitch(h.id)}
                        disabled={busy}
                        data-testid={`household-switch-option-${h.id}`}
                        className={`w-full text-left tup-card p-4 flex items-center justify-between gap-3 border-2 transition-colors ${
                          h.is_active
                            ? "border-[#277A82] bg-[#DCEDF0]/50"
                            : "border-transparent hover:border-[#277A82]/40 hover:bg-[#F5F3ED]"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                              h.is_active ? "bg-[#1F5566] text-white" : "bg-[#F1EEE5] text-[#4F5D75]"
                            }`}
                          >
                            <RowIcon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-[#2D3142] truncate">
                              {h.name}
                            </div>
                            <div className="text-[10px] text-[#8A93A6]">
                              {h.members.length} member{h.members.length === 1 ? "" : "s"}
                            </div>
                          </div>
                        </div>
                        {h.is_active && <Check className="w-4 h-4 text-[#1F5566] shrink-0" />}
                      </button>
                    </li>
                    );
                  })}
                </ul>
              )}

              {sheet === "create" && (
                <div className="space-y-3" data-testid="household-create-form">
                  <p className="text-sm text-[#4F5D75]">
                    Give your new household a name. You'll be its first owner.
                  </p>
                  <input
                    type="text"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doCreate()}
                    placeholder="e.g., Beach House"
                    data-testid="household-create-input"
                    maxLength={80}
                    className="w-full bg-white border border-[#E2E0D8] rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#277A82] focus:ring-1 focus:ring-[#277A82]/40"
                  />
                  <button
                    type="button"
                    onClick={doCreate}
                    disabled={busy || !createName.trim()}
                    data-testid="household-create-submit"
                    className="w-full brand-gradient-bg text-white font-semibold rounded-xl h-12 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Create household
                  </button>
                </div>
              )}

              {sheet === "join" && (
                <div className="space-y-3" data-testid="household-join-form">
                  <p className="text-sm text-[#4F5D75]">
                    Enter the 6-character join code shared by an owner.
                  </p>
                  <input
                    type="text"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && doJoin()}
                    placeholder="ABC123"
                    data-testid="household-join-input"
                    maxLength={12}
                    className="w-full bg-white border border-[#E2E0D8] rounded-xl px-3 py-3 text-base font-mono tracking-[0.3em] uppercase text-center focus:outline-none focus:border-[#277A82] focus:ring-1 focus:ring-[#277A82]/40"
                  />
                  <button
                    type="button"
                    onClick={doJoin}
                    disabled={busy || joinCode.trim().length < 4}
                    data-testid="household-join-submit"
                    className="w-full brand-gradient-bg text-white font-semibold rounded-xl h-12 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                    Join household
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <HouseholdSwitchTransition
        open={!!switching}
        fromName={switching?.from}
        toName={switching?.to}
        fromIcon={switching?.fromIcon}
        toIcon={switching?.toIcon}
      />
    </>
  );
};

export default MultiHouseholdPanel;
